import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Settings scoped to THIS physical device (browser), not to the signed-in user.
// Handheld scanners with built-in keypads (e.g. Honeywell EDA61K) don't need the
// on-screen numberpad, while touch-only phones do — that's a property of the
// hardware, so it persists in localStorage and survives sign-out.
export type ShowNumberpadOverride = 'default' | 'on' | 'off';

export interface DeviceSettings {
  showNumberpad?: ShowNumberpadOverride;
  // "Local Inventory Cache": this device keeps the catalog in IndexedDB and
  // queues transfer changes, so scanning keeps working through wifi drops.
  localInventoryCache?: boolean;
  // Stable per-browser identity for per-device counts on transfers.
  deviceId?: string;
}

const STORAGE_KEY = 'deviceSettings';

@Injectable({
  providedIn: 'root'
})
export class DeviceSettingsService {

  private settings: DeviceSettings = {};
  private settingsChanged = new Subject<DeviceSettings>();

  constructor() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      this.settings = stored ? JSON.parse(stored) : {};
    } catch (e) {
      this.settings = {};
    }
  }

  settingsChangedObservable(): Observable<DeviceSettings> {
    return this.settingsChanged.asObservable();
  }

  isLocalInventoryCacheEnabled(): boolean {
    return this.settings.localInventoryCache === true;
  }

  setLocalInventoryCache(enabled: boolean) {
    this.settings.localInventoryCache = enabled;
    this.persist();
  }

  // Generated once per browser and kept for good: it keys this device's share
  // of every counted line, so it must survive sign-out and reloads.
  getDeviceId(): string {
    if (!this.settings.deviceId) {
      const random = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID().replace(/-/g, '')
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      this.settings.deviceId = 'd' + random.slice(0, 31);
      this.persist();
    }
    return this.settings.deviceId;
  }

  getShowNumberpadOverride(): ShowNumberpadOverride {
    return this.settings.showNumberpad || 'default';
  }

  setShowNumberpadOverride(value: ShowNumberpadOverride) {
    this.settings.showNumberpad = value;
    this.persist();
  }

  // Resolve the effective flag: an explicit device override wins, otherwise the
  // client Configuration default applies (true when the client doesn't set one).
  showNumberpad(clientDefault: boolean): boolean {
    switch (this.getShowNumberpadOverride()) {
      case 'on':
        return true;
      case 'off':
        return false;
      default:
        return clientDefault;
    }
  }

  private persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (e) {
      console.error('Failed to persist device settings:', e);
    }
    this.settingsChanged.next(this.settings);
  }
}
